import { gql } from '@apollo/client';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { CSV_MAPPINGS_FIELDS } from '@/graphql/queries/csv_mappings';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';
import { JOB_FIELDS } from '@/graphql/queries/jobs';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { PRICING_RUN_FIELDS } from '@/graphql/fragments/pricing_runs';
import { PRICING_RUN_INSTANCE_FIELDS } from '@/graphql/fragments/pricing_run_instances';
import { PRICING_OVERRIDE_FIELDS } from '@/graphql/fragments/pricing_overrides';
import { ITEM_DIMENSIONS_FIELDS } from '@/graphql/fragments/item_dimensions';
import { SERVICES_FIELDS } from '@/graphql/fragments/services';

export const QUERY_MAPPINGS_BY_USER = gql`
    ${CSV_MAPPINGS_FIELDS}

    query CsvMappingByUserIdAndType($user_id: uuid!, $freight_type: String!) {
        results: csv_mappings(where: { _and: { user_id: { _eq: $user_id }, freight_type: { _eq: $freight_type } } }) {
            ...CsvMappingsFields
        }
    }
`;

export const QUERY_CLIENTS_BY_USER = gql`
    ${CLIENT_FIELDS}
    ${LOCATION_FIELDS}

    query ClientByUuid($client_id: uuid!) {
        result: clients(where: { client_id: { _eq: $client_id } }) {
            ...ClientFields
            locations {
                ...LocationFields
            }
        }
    }
`;

export const QUERY_CLIENTS_BY_USERS = gql`
    ${CLIENT_FIELDS}
    ${LOCATION_FIELDS}

    query ClientsByUuid($client_ids: [uuid!]!) {
        results: clients(where: { client_id: { _in: $client_ids } }) {
            ...ClientFields

            locations {
                ...LocationFields
            }
        }
    }
`;

export const QUERY_JOBS_BY_CLIENT = gql`
    ${JOB_FIELDS}
    ${PRICING_RUN_FIELDS}
    ${PRICING_RUN_INSTANCE_FIELDS}
    ${ORDER_FIELDS}
    ${CLIENT_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}

    query JobsByClientId($client_ids: [uuid!]!, $start_cutoff: timestamptz!) {
        result: jobs(
            where: {
                _and: [
                    { _or: [{ client_id: { _in: $client_ids } }, { partner_client_id: { _in: $client_ids } }] }
                    { created_at: { _gte: $start_cutoff } }
                ]
            }
        ) {
            ...JobFields

            orders {
                ...OrderFields

                last_pricing_run {
                    ...PricingRunFields

                    instances(where: { status: { _eq: "FAILED" } }) {
                        ...PricingRunInstanceFields

                        override {
                            ...PricingOverrideFields
                        }

                        partner {
                            ...ClientFields
                        }
                    }
                }
            }
        }
    }
`;

export const FETCH_LOGOS = gql`
    ${WAREHOUSE_EVENT_FIELDS}

    query PricingOverridesById($shipper_id: uuid!, $cutoff_ts: timestamptz) {
        results: pricing_overrides(where: { client_id: { _eq: $shipper_id } }) {
            pricing_override_id
            algo_type
            override_type
            marketplace_tier
            assign_id

            partner_client {
                client_id
                business_name
                profile_img
                profile_img_color
            }

            assign {
                client_id
                business_name
                profile_img
                profile_img_color

                carried_orders(
                    where: { _and: [{ shipper_id: { _eq: $shipper_id } }, { created_at: { _gte: $cutoff_ts } }] }
                ) {
                    order_id
                    preferred_driver_id

                    shipper_rate_override {
                        pricing_override_id
                        algo_type
                    }

                    wh_events(
                        order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]
                    ) {
                        ...WarehouseEvents

                        transition {
                            next
                            current
                        }
                    }
                }
            }
        }
    }
`;

export const QUERY_JOB_BY_ID = gql`
    ${JOB_FIELDS}
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${PRICING_RUN_FIELDS}
    ${PRICING_RUN_INSTANCE_FIELDS}
    ${CLIENT_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    ${SERVICES_FIELDS}

    query JobsByClientId($job_id: uuid!) {
        result: jobs(where: { job_id: { _eq: $job_id } }) {
            ...JobFields

            orders(
                where: {
                    _or: [
                        {
                            _and: [
                                {
                                    order_status: {
                                        _in: ["pending_confirmation", "incomplete", "processing", "duplicate"]
                                    }
                                }
                                { source_form: { _neq: "APIv2" } }
                            ]
                        }
                        {
                            _and: [
                                { order_status: { _in: ["pending_confirmation", "incomplete", "processing"] } }
                                { source_form: { _eq: "APIv2" } }
                            ]
                        }
                    ]
                }
                order_by: { order_number: asc }
            ) {
                ...OrderFields

                service_catalog_filter

                order_carrier {
                    client_id
                    business_name
                    profile_img
                    profile_img_color
                }

                last_pricing_run {
                    ...PricingRunFields

                    instances(where: { status: { _eq: "FAILED" } }) {
                        ...PricingRunInstanceFields

                        override {
                            ...PricingOverrideFields

                            assign {
                                client_id
                                business_name
                                profile_img
                                profile_img_color
                            }
                        }

                        partner {
                            ...ClientFields
                        }
                    }
                }

                itemsByOrderId {
                    ...ItemFields
                }

                services {
                    ...ServicesFields
                }

                returns @client {
                    ...ItemFields
                }

                items @client {
                    ...ItemFields
                }
            }
        }
    }
`;

export const QUERY_LOCATIONS_BY_CLIENT_ID = gql`
    ${LOCATION_FIELDS}

    query LocationsByClientId($client_id: uuid!) {
        locations(where: { client_id: { _eq: $client_id } }) {
            ...LocationFields
        }
    }
`;

export const QUERY_DIMENSIONS_BY_SKU = gql`
    ${ITEM_DIMENSIONS_FIELDS}

    query DimensionsBySku($sku: String!, $client_id: uuid!) {
        result: item_dimensions(where: { sku: { _eq: $sku }, client_id: { _eq: $client_id } }) {
            ...ItemDimensionsFields
            # Inline rather than in the shared fragment — onward-functions consumes that too.
            description
        }
    }
`;

// QUERY_DIMENSIONS_BY_SKU for a whole CSV import in one round trip.
export const QUERY_DIMENSIONS_BY_SKUS = gql`
    ${ITEM_DIMENSIONS_FIELDS}

    query DimensionsBySkus($skus: [String!]!, $client_id: uuid!) {
        results: item_dimensions(where: { sku: { _in: $skus }, client_id: { _eq: $client_id } }) {
            ...ItemDimensionsFields
            description
        }
    }
`;

// Unlimited by design: a capped list silently hides most of a shipper's catalog from the
// autocomplete's search. If catalogs outgrow a few thousand rows, move to the debounced
// _ilike pattern in Account/SKUs/hooks.js.
export const QUERY_ITEM_SKU = gql`
    query SkuByClientId($client_id: uuid!) {
        results: item_dimensions(where: { client_id: { _eq: $client_id } }, order_by: { sku: asc_nulls_last }) {
            item_dimension_id
            sku
        }
    }
`;

export const QUERY_SHIPPERS_BY_ORGANIZATION = gql`
    query ShippersByOrganization($organization_id: uuid!) {
        results: clients(
            where: { user: { organization_id: { _eq: $organization_id }, roles: { _has_key: "SHIPPER" } } }
            order_by: { business_name: asc }
        ) {
            client_id
            business_name
        }
    }
`;
